import { NotificationType } from '@electricjs/core_entity-client';
import { Trans } from 'react-i18next';

import i18n from '@/i18n';

import { useGetRequestData } from '@/hooks/useGetRequestData';
import { EmployeeDirectoryListing } from '@/types/employees';
import { SystemRequester } from '@/types/requests';

import { EmailDetail } from '../EmailDetail';
import { GroupDetail } from '../GroupDetail';
import { NotificationItemHeader } from '../NotificationItemHeader';
import { RequestActions } from '../RequestActions/RequestActions';
import { NotificationItemProps } from '../types';
import HRISXboardingNotificationIcon from './HRISXboardingNotificationIcon';
import { getDisplayFullName } from 'common/utils/getFullName';

const getTitle = (
  employee?: EmployeeDirectoryListing,
  systemRequester?: SystemRequester
) => {
  const employeeName = employee
    ? getDisplayFullName(employee)
    : i18n.t('common:notificationItems.someone');

  const systemRequesterName =
    systemRequester?.name ?? i18n.t('common:notificationItems.yourHris');

  return (
    <Trans
      i18nKey="notificationItems.hrisOffboarding.title"
      ns="common"
      values={{ employeeName, systemRequesterName }}
      components={{ bold: <b /> }}
    />
  );
};

const HRISOffboardingNotificationItem = ({
  notification,
  hideDetails,
}: NotificationItemProps) => {
  const { data: requestData, isLoading } = useGetRequestData({
    requestId: notification?.data?.request_id ?? null,
    options: {
      includeEmployee: true,
      includeEmployeeGroups: true,
      includeSystemRequester: true,
    },
  });

  const employee = requestData?.employee;
  const employeeGroups = requestData?.employeeGroups;
  const employeeEmail = employee?.email;
  const systemRequester = requestData?.systemRequester;

  const getLabel = () => {
    switch (notification.type) {
      case NotificationType.RequestAssigned:
        return i18n.t('common:notificationItems.labels.requestAssigned');

      case NotificationType.RequestCompleted:
        return i18n.t('common:notificationItems.labels.requestCompleted');
      default:
        return '';
    }
  };

  return (
    <NotificationItemHeader
      notification={notification}
      isLoading={isLoading}
      title={getTitle(employee, systemRequester)}
      icon={
        <HRISXboardingNotificationIcon
          name={systemRequester?.name}
          isNotificationUnseen={!notification.seenAt}
          loading={isLoading}
        />
      }
      label={getLabel()}>
      {!hideDetails ? (
        <>
          <GroupDetail isLoading={isLoading} groups={employeeGroups} />
          <EmailDetail isLoading={isLoading} email={employeeEmail} />
          <RequestActions
            loading={isLoading}
            request={requestData.request}
            requestType={requestData.requestType}
          />
        </>
      ) : null}
    </NotificationItemHeader>
  );
};

export default HRISOffboardingNotificationItem;
